/*
 * ADKFF: keep third-party floating widgets from covering each other or the
 * buy/checkout buttons.
 *
 * Widgets handled (all injected by app embeds, not by the theme):
 *   - Klaviyo signup teaser  ("Claim special offers" tab, bottom-left)
 *   - Gorgias live chat launcher (bottom-left, business hours only)
 *   - Google merchant / customer-reviews badge (bottom corner)
 *
 * Obstacles they must never cover:
 *   - Wonder sticky buy bar on product pages (mobile)
 *   - Monster cart drawer, Wonder cart drawer, quick-buy drawer (checkout button)
 *
 * Lives in custom.js (loaded deferred by layout/theme.liquid). Positions are
 * written as CSS custom properties / classes on <html>; the rules that use
 * them live in assets/custom.css. Nothing here changes app settings, and when
 * nothing conflicts the widgets are left exactly where the apps put them.
 */
(function () {
  'use strict';

  var root = document.documentElement;
  var GAP = 12;
  var EDGE = 16;

  var SEL = {
    teaser: '[class*="kl-teaser-"]',
    chat: '#gorgias-chat-container #chat-button',
    google: [
      'iframe[src*="merchantwidget"]',
      'iframe[id^="merchantwidget"]',
      '#merchantwidget',
      '[id^="merchant-widget"]',
      'iframe[src*="ratingbadge"]',
      '[id^="google-customer-reviews"]'
    ].join(','),
    stickyBar: '.wt-product__sticky-buy--show',
    drawers: [
      '#monster-cart-wrapper:not(.mu_closed)',
      '#CartDrawer.is-open',
      '#CartDrawer.active',
      '.wt-cart__drawer[open]',
      '.wt__quick-buy__container.is-open',
      '.wt__quick-buy__container.active'
    ].join(',')
  };

  // Offsets currently applied by our CSS, so measured rects can be un-offset
  // without toggling styles (which would restart transitions).
  var applied = { base: 0, googleLift: 0 };
  // Widest teaser width seen, so the overlap test does not depend on which
  // style (tab or pill) is currently applied.
  var teaserMaxWidth = 0;
  var flips = 0;

  function onScreen(r) {
    return r.width >= 2 && r.height >= 2 && r.bottom > 0 && r.top < window.innerHeight &&
      r.right > 0 && r.left < window.innerWidth;
  }

  // ignoreOurHide: a widget we hid ourselves still counts as present.
  function measure(el, ignoreOurHide) {
    if (!el) return null;
    var s = getComputedStyle(el);
    if (s.display === 'none') return null;
    if (!ignoreOurHide && (s.visibility === 'hidden' || parseFloat(s.opacity) === 0)) return null;
    var r = el.getBoundingClientRect();
    return onScreen(r) ? r : null;
  }

  function firstMeasured(selector, ignoreOurHide) {
    var list = document.querySelectorAll(selector);
    for (var i = 0; i < list.length; i++) {
      var r = measure(list[i], ignoreOurHide);
      if (r) return r;
    }
    return null;
  }

  function shift(r, dy) {
    return { left: r.left, right: r.right, top: r.top + dy, bottom: r.bottom + dy, width: r.width, height: r.height };
  }

  function intersects(a, b) {
    return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
  }

  function setVar(name, px) {
    var value = Math.round(px) + 'px';
    if (root.style.getPropertyValue(name) !== value) root.style.setProperty(name, value);
  }

  function toggle(cls, on) {
    if (root.classList.contains(cls) !== on) root.classList.toggle(cls, on);
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      layout();
    });
  }

  function layout() {
    var vw = window.innerWidth;
    var vh = window.innerHeight;

    // 1. Sticky buy bar: everything floating rides above it. Opacity is
    // ignored because the bar fades in; its box is final immediately.
    var barEl = document.querySelector(SEL.stickyBar);
    var bar = barEl && getComputedStyle(barEl).display !== 'none' ? barEl.getBoundingClientRect() : null;
    var base = bar && onScreen(bar) && bar.bottom >= vh - 2 ? Math.ceil(vh - bar.top) : 0;

    // 2. Open drawers.
    var drawers = [];
    document.querySelectorAll(SEL.drawers).forEach(function (d) {
      var r = measure(d, false);
      if (r) drawers.push(r);
    });

    // 3. Widgets, un-offset to where their apps placed them.
    var chat = firstMeasured(SEL.chat, root.classList.contains('adk-hide-chat'));
    if (chat) chat = shift(chat, applied.base);
    var google = firstMeasured(SEL.google, root.classList.contains('adk-hide-google'));
    if (google) google = shift(google, applied.base + applied.googleLift);
    var teaserEl = document.querySelector(SEL.teaser);
    var teaser = teaserEl ? measure(teaserEl, root.classList.contains('adk-hide-teaser')) : null;

    // Google badge in the same corner as the chat launcher: stack it above.
    var googleLift = 0;
    if (google && chat && intersects(google, chat)) {
      googleLift = Math.ceil(google.bottom - chat.top + GAP);
    }

    var hits = function (r) {
      return drawers.some(function (d) { return intersects(r, d); });
    };
    toggle('adk-hide-chat', !!chat && hits(shift(chat, -base)));
    toggle('adk-hide-google', !!google && hits(shift(google, -(base + googleLift))));

    setVar('--adk-float-base', base);
    setVar('--adk-google-lift', googleLift);
    applied.base = base;
    applied.googleLift = googleLift;

    if (!teaser) {
      toggle('adk-teaser-moved', false);
      toggle('adk-teaser-float', false);
      toggle('adk-hide-teaser', false);
      return;
    }

    // 4. Teaser. Left alone unless something is in its way.
    teaserMaxWidth = Math.max(teaserMaxWidth, Math.ceil(teaser.width));
    var tw = teaserMaxWidth;
    var th = teaser.height;
    var left = 20; // Klaviyo's own margin, so a lifted tab stays in line
    var bottom = base;
    var floating = false;

    if (chat && chat.left + chat.width / 2 < vw / 2) {
      var besideLeft = Math.ceil(chat.right + GAP);
      if (besideLeft + tw + EDGE <= vw) {
        left = besideLeft;
        bottom = Math.max(0, Math.round(vh - chat.bottom + (chat.height - th) / 2)) + base;
      } else {
        left = Math.max(EDGE, Math.round(chat.left));
        bottom = Math.ceil(vh - chat.top + GAP) + base;
      }
      floating = true;
    }

    if (google) {
      var g = shift(google, -(base + googleLift));
      var spot = { left: left, right: left + tw, top: vh - bottom - th, bottom: vh - bottom };
      if (intersects(spot, g)) {
        bottom = Math.ceil(vh - g.top + GAP);
        floating = true;
      }
    }

    var moved = floating || base > 0;
    setVar('--adk-teaser-left', left);
    setVar('--adk-teaser-bottom', bottom);
    toggle('adk-teaser-moved', moved);

    var wasFloating = root.classList.contains('adk-teaser-float');
    toggle('adk-teaser-float', floating);
    // The pill and the tab differ in height; settle once, never loop.
    if (wasFloating !== floating && flips < 2) {
      flips++;
      schedule();
    } else {
      flips = 0;
    }

    var spotNow = { left: left, right: left + tw, top: vh - bottom - th, bottom: vh - bottom };
    toggle('adk-hide-teaser', hits(spotNow));
  }

  function start() {
    // Widgets arrive late and drawers open by class changes.
    new MutationObserver(schedule).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'open']
    });
    var resized = function () {
      teaserMaxWidth = 0; // tab size differs by breakpoint
      schedule();
    };
    window.addEventListener('resize', resized, { passive: true });
    if (window.visualViewport) window.visualViewport.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('orientationchange', resized);
    // Drawers and the sticky bar animate; re-measure when they settle.
    var settled = function (e) {
      var t = e.target;
      if (t && t.closest && t.closest(SEL.drawers + ',' + SEL.stickyBar + ',#monster-cart-wrapper,#CartDrawer,.wt__quick-buy__container')) schedule();
    };
    document.addEventListener('transitionend', settled, true);
    document.addEventListener('animationend', settled, true);
    schedule();
    // Gorgias renders after its own business-hours check.
    if (window.GorgiasChat && window.GorgiasChat.init) {
      window.GorgiasChat.init().then(schedule, function () {});
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
  window.adkFloatingLayout = schedule;
})();
